// src/forms/sms_optin_intl_tel.js

import DOMPurify from 'dompurify';
import intlTelInput from "intl-tel-input/intlTelInputWithUtils";

const telInputCss = require('../styles/tel_input.css').default;

// Uses a Handlebars conditional so the "from <org>" clause cleanly disappears when
// organization_metadata.organization_name is missing (Handlebars must be loaded for the
// conditional to fully suppress the clause; the fallback parser will still resolve the
// inner token, only failing in the narrow case of "no Handlebars + missing name").
export const SMS_OPTIN_LABEL_DEFAULT_TEXT = 'Sign up for SMS messages & alerts{{#if organization_metadata.organization_name}} from {{organization_metadata.organization_name}}{{/if}}. Reply STOP to cancel. Msg & data rates apply.';

// Lenient cap — covers ~6-7 sentences of legal disclosure language.
const SMS_OPTIN_LABEL_MAX_LENGTH = 1000;

/**
 * Normalize an SMS opt-in label string after Handlebars expansion.
 * Callers should render via {@link render_sms_optin_label_html_into_element} so optional
 * `<a href="https://...">` segments (only https, after sanitization) are shown as links;
 * all other markup is stripped by DOMPurify.
 *
 * @param {*} raw
 * @returns {string|null}
 */
export function normalize_sms_optin_label_text(raw) {
  if (typeof raw !== 'string') return null;
  const collapsed = raw.replace(/\s+/g, ' ').trim();
  if (!collapsed) return null;
  if (collapsed.length > SMS_OPTIN_LABEL_MAX_LENGTH) {
    return collapsed.slice(0, SMS_OPTIN_LABEL_MAX_LENGTH);
  }
  return collapsed;
}

/**
 * Inject `sms_optin:full` into a user_fields string when missing.
 *
 * Used so an org that sets data-hub-sms-optin-label-text without remembering to add
 * `sms_optin` to data-hub-form-user-fields still gets the SMS block rendered. Inserts
 * right before the first `submit` / `submit_button` token so the resulting string is
 * identical to what the org would have hand-written.
 *
 * The size suffix is irrelevant for SMS rendering (the block is always full-width and
 * is filtered out before per-field render), but we keep it for visual consistency
 * with other field tokens.
 *
 * @param {*} user_fields_string
 * @returns {string}
 */
export function inject_sms_optin_into_user_fields(user_fields_string) {
  const sms_token = 'sms_optin:full';
  if (typeof user_fields_string !== 'string' || !user_fields_string.trim()) {
    return sms_token;
  }

  const parts = user_fields_string
    .split(',')
    .map(p => p.trim())
    .filter(p => p !== '');

  if (parts.some(p => p.split(':')[0].trim() === 'sms_optin')) {
    return parts.join(',');
  }

  const submit_index = parts.findIndex(p => {
    const name = p.split(':')[0].trim();
    return name === 'submit' || name === 'submit_button';
  });

  if (submit_index === -1) {
    parts.push(sms_token);
  } else {
    parts.splice(submit_index, 0, sms_token);
  }
  return parts.join(',');
}

/**
 * Resolve the final label text we render AND log to form_metadata.
 * Both arguments are expected to have been pre-templated through processHandlebarsTemplate
 * by the caller (so {{#if}} conditionals on organization_metadata.organization_name are
 * honored when real Handlebars is available on the page).
 *
 * @param {*} templated_custom_text
 * @param {*} templated_default_text
 * @returns {string}
 */
export function resolve_sms_optin_label_text(templated_custom_text, templated_default_text) {
  return normalize_sms_optin_label_text(templated_custom_text)
    || normalize_sms_optin_label_text(templated_default_text)
    || SMS_OPTIN_LABEL_DEFAULT_TEXT;
}

const sms_optin_label_dom_purify_config = {
  ALLOWED_TAGS: ['a'],
  ALLOWED_ATTR: ['href'],
};

function unwrap_sms_optin_anchor_to_plain_text(anchor_element) {
  const replacement_text = anchor_element.textContent ?? '';
  const text_node = anchor_element.ownerDocument.createTextNode(replacement_text);
  anchor_element.parentNode?.replaceChild(text_node, anchor_element);
}

/**
 * After DOMPurify, keep only https: URLs (resolved against document.baseURI for relative paths).
 * Drops javascript:, data:, http:, missing href, or malformed URLs by unwrapping to plain text.
 * Always forces `target="_blank"` and `rel="noopener noreferrer"` (DOMPurify strips any org-supplied target).
 *
 * @param {HTMLElement} container_element
 */
function enforce_https_href_on_sms_optin_label_anchors(container_element) {
  const base_for_relative_urls =
    typeof document !== 'undefined' && typeof document.baseURI === 'string'
      ? document.baseURI
      : 'https://example.invalid/';
  container_element.querySelectorAll('a').forEach((anchor_element) => {
    const raw_href = anchor_element.getAttribute('href');
    if (typeof raw_href !== 'string' || raw_href.trim() === '') {
      unwrap_sms_optin_anchor_to_plain_text(anchor_element);
      return;
    }
    try {
      const resolved_url = new URL(raw_href.trim(), base_for_relative_urls);
      if (resolved_url.protocol !== 'https:') {
        unwrap_sms_optin_anchor_to_plain_text(anchor_element);
        return;
      }
      anchor_element.setAttribute('href', resolved_url.href);
      // New tab only — org HTML cannot set target (stripped by DOMPurify); consent links stay out of the form navigation context.
      anchor_element.setAttribute('target', '_blank');
      anchor_element.setAttribute('rel', 'noopener noreferrer');
      anchor_element.classList.add('text-primary-brand-color', 'underline');
    } catch {
      unwrap_sms_optin_anchor_to_plain_text(anchor_element);
    }
  });
}

/**
 * Renders resolved SMS opt-in label copy into a span: plain text stays plain; limited `<a href>`
 * links are allowed (https only). Allowed anchors always get `target="_blank"` and
 * `rel="noopener noreferrer"`. Expects Handlebars (if any) to already be expanded by the caller.
 *
 * @param {HTMLElement} target_element
 * @param {string} resolved_label_text
 * @param {string} fallback_plain_text
 */
export function render_sms_optin_label_html_into_element(
  target_element,
  resolved_label_text,
  fallback_plain_text
) {
  const label_source =
    (typeof resolved_label_text === 'string' && resolved_label_text.trim())
      ? resolved_label_text
      : fallback_plain_text;
  const sanitized_html_string = DOMPurify.sanitize(label_source, sms_optin_label_dom_purify_config);
  const temporary_holder = document.createElement('div');
  temporary_holder.innerHTML = sanitized_html_string;
  enforce_https_href_on_sms_optin_label_anchors(temporary_holder);
  target_element.replaceChildren();
  while (temporary_holder.firstChild) {
    target_element.appendChild(temporary_holder.firstChild);
  }
  // Second pass on the mounted subtree so every `<a>` reliably opens in a new browsing context.
  enforce_https_href_on_sms_optin_label_anchors(target_element);
}

const get_browser_locale_country_code = () => {
	const locale_candidates = [];

	if (typeof navigator !== 'undefined') {
		if (Array.isArray(navigator.languages)) {
			locale_candidates.push(...navigator.languages);
		}

		if (typeof navigator.language === 'string') {
			locale_candidates.push(navigator.language);
		}
	}

	if (typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function') {
		const resolved_locale = Intl.DateTimeFormat().resolvedOptions?.().locale;
		if (typeof resolved_locale === 'string') {
			locale_candidates.push(resolved_locale);
		}
	}

	for (const locale_candidate of locale_candidates) {
		const locale_match = String(locale_candidate).match(/[-_]([a-zA-Z]{2})\b/);
		if (locale_match?.[1]) {
			return locale_match[1].toUpperCase();
		}
	}

	return null;
};

/**
 * Generates a nicely formatted SMS opt-in block that uses intl-tel-input.
 *
 * @param {boolean} allow_prefill
 * @param {Set<string>} set_of_form_inputs_added
 * @param {HTMLFormElement} form
 * @param {Object} data_hub_instance
 * @param {string} [resolved_label_text] - already-resolved (custom or default) label text. Caller
 *   should pre-resolve via resolve_sms_optin_label_text so the same string is logged to form_metadata.
 *   Rendered as text plus optional https `<a href>` only (see {@link render_sms_optin_label_html_into_element}).
 * @returns {HTMLDivElement} the SMS opt-in block element
 */
export function generate_sms_optin_intl_tel(
  allow_prefill = true,
  set_of_form_inputs_added,
  form,
  data_hub_instance = window?.data_hub,
  resolved_label_text = SMS_OPTIN_LABEL_DEFAULT_TEXT
) {
  if (!form) {
    data_hub_instance?.error_function?.('No form provided to generate_sms_optin_intl_tel', null);
    return;
  }

  if (set_of_form_inputs_added.has('cell_phone')) {
    form.querySelectorAll('input[name="cell_phone"]').forEach(input => {
      input.parentElement?.remove();
      set_of_form_inputs_added.delete('cell_phone');
    });
  }

  // Stacked layout: full-width phone row, then full-width opt-in row (no side-by-side float).
  const sms_optin_div = document.createElement('div');
  sms_optin_div.classList.add(
    'flex', 'flex-col', 'w-full', 'mb-4', 'mt-4', 'gap-3', 'sms_optin_container'
  );

  const cell_phone_div = document.createElement('div');
  cell_phone_div.classList.add('w-full');

  const cell_phone_input = document.createElement('input');
  cell_phone_input.type = 'tel';
  cell_phone_input.classList.add(
    'w-full', 'p-2', 'border', 'border-grey-brand-color-400', 'rounded-md'
  );
  cell_phone_input.placeholder = 'Cell Phone';
  cell_phone_input.name = 'cell_phone';
  cell_phone_input.autocomplete = 'tel';
  if (allow_prefill) {
    cell_phone_input.dataset.allowDataHubPrefill = 'true';
  }
  cell_phone_input.dataset.removeForUnsub = 'true';
  cell_phone_input.id = 'cell_phone';

  cell_phone_div.appendChild(cell_phone_input);

  // Create SMS opt-in checkbox container.
  const sms_optin_checkbox_div = document.createElement('div');
  sms_optin_checkbox_div.classList.add('w-full', 'flex', 'flex-row', 'items-start', 'gap-2', 'mb-4');

  const sms_optin_checkbox = document.createElement('input');
  sms_optin_checkbox.type = 'checkbox';
  sms_optin_checkbox.name = 'sms_optin';
  // Default unchecked; prefill_form sets checked only when user_data.sms_optin is true from
  // validated sources (JWT / HMAC GET, CORS payload, constructor merge) — never from URL params.
  sms_optin_checkbox.checked = false;
  if (allow_prefill) {
    sms_optin_checkbox.dataset.allowDataHubPrefill = 'true';
  }
  sms_optin_checkbox.dataset.removeForUnsub = 'true';
  sms_optin_checkbox.classList.add(
    'w-5',
    'h-5',
    'sms_optin_checkbox',
    'flex',
    'shrink-0',
    'mt-0.5',
    'accent-primary-brand-color',
    'border',
    'border-grey-brand-color-400',
    'rounded'
  );
  sms_optin_checkbox.id = 'sms_optin';

  const sms_optin_label = document.createElement('label');
  sms_optin_label.htmlFor = 'sms_optin';
  sms_optin_label.classList.add(
    'text-gray-700', 'text-sm', 'flex-1', 'min-w-0', 'flex', 'flex-col', 'items-start'
  );

  const sms_optin_label_text_span = document.createElement('span');
  render_sms_optin_label_html_into_element(
    sms_optin_label_text_span,
    resolved_label_text,
    SMS_OPTIN_LABEL_DEFAULT_TEXT
  );
  sms_optin_label_text_span.classList.add('sms_optin_label_text');

  sms_optin_label.appendChild(sms_optin_label_text_span);

  sms_optin_checkbox_div.appendChild(sms_optin_checkbox);
  sms_optin_checkbox_div.appendChild(sms_optin_label);

  sms_optin_div.appendChild(cell_phone_div);
  sms_optin_div.appendChild(sms_optin_checkbox_div);

  set_of_form_inputs_added.add('cell_phone');
  set_of_form_inputs_added.add('sms_optin');

  return sms_optin_div;
}

/**
 * Gets the user's country code from data_hub re-check since was run async during page_metadata creation
 * @param {Function} callback
 * @returns {Function} callback
 */
// export function re_get_user_country_code_with_tel_callback(callback) {
//   const countryCode = window?.data_hub?.user_data?.country_code ||
//     window?.data_hub?.page_metadata?.ip_data?.country_code;

//   if (countryCode) {
//     callback(countryCode);
//   } else {
//     console.warn('DEBUG Country code could not be retrieved');
//     callback('US');
//   }
// }

/**
 * Inject the tel-input-intl css into the parent document.
 * @param {HTMLElement} container - The container to inject CSS into.
 * @param {Object} data_hub_instance - The data hub instance for logging errors.
 */
export const inject_tel_intl_input_css = (container, data_hub_instance = window?.data_hub) => {
  if (!container) {
    data_hub_instance?.error_function?.('No container provided to inject inject_tel_intl_input_css CSS', null);
    // Find the top most div inside document, or fallback to head for styles
    container = document.querySelector('.data-hub-container') || 
                document.querySelector('.data-hub-form-container') || 
                document.head;
    if (!container) {
      data_hub_instance?.error_function?.('No valid container found to inject_tel_intl_input_css CSS', null);
      return;
    }
  }
  
  // Check if style already exists anywhere in the document to prevent duplicates
  if (!document.querySelector('style[data-intl-tel-input]')) {
    const style = document.createElement('style');
    style.textContent = telInputCss;
    style.setAttribute('data-intl-tel-input', '');
    style.type = 'text/css';
    container.appendChild(style);
  }
}

/**
 * Bind all telephone inputs in a container with intl-tel-input
 * Track user interaction to prevent unwanted value updates
 * @param {HTMLElement} data_hub_most_parent_container - The container to search for tel inputs
 * @param {Object} data_hub_instance - The data hub instance
 * @returns {Array} - Array of intl-tel-input instances
 */
export const bind_all_tel_inputs_intl = async (
  data_hub_most_parent_container,
  data_hub_instance = window?.data_hub
) => {
  if (!data_hub_most_parent_container) {
    data_hub_instance?.error_function?.('No container provided to bind_all_tel_inputs_intl', null);
    return [];
  }

  const country_code =
    data_hub_instance?.user_data?.country_code ||
    data_hub_instance?.page_metadata?.ip_data?.country_code ||
    window?.data_hub_user_data?.country_code ||
    get_browser_locale_country_code() ||
    'US';

  const tel_inputs = data_hub_most_parent_container.querySelectorAll('input[type="tel"]');
  if (!tel_inputs || tel_inputs.length === 0) {
    return [];
  }

  // Inject the tel-input-intl css into the parent document.
  inject_tel_intl_input_css(data_hub_most_parent_container, data_hub_instance);

  if (typeof intlTelInput !== 'function') {
    data_hub_instance?.error_function?.('intlTelInput is not a function' + typeof intlTelInput, null);
    return [];
  }

  // Reuse an existing dropdown container if one is already attached to this parent.
  // Without this guard, every call appends a fresh `iti__country-list--independent` div —
  // dormant today (renderForm runs once per element) but a leak waiting to happen for
  // any future re-render path.
  let drop_down_container = data_hub_most_parent_container.querySelector(':scope > .iti__country-list--independent');
  if (!drop_down_container) {
    drop_down_container = document.createElement('div');
    drop_down_container.classList.add('iti__country-list--independent');
    data_hub_most_parent_container.appendChild(drop_down_container);
  }

  intlTelInput.defaults.dropdownContainer = drop_down_container;

  // Use Promise.all to wait for all initialization promises.
  const iti_inputs = await Promise.all(
    Array.from(tel_inputs).map(async (input) => {
      // Optionally check if already initialized:
      if (input.dataset.intlTelInputInit) {
        return input.itiInstance; // if previously attached
      }

      input.dataset.intlTelInputInit = true;
      input.dataset.userInteracted = "false";
      input.dataset.focused = "false";

      const itiInstance = intlTelInput(input, {
        initialCountry: country_code || "US",
        countryOrder: ["us", "gb", "ca", "au"],
        placeholderNumberType: 'MOBILE',
        showFlags: true,
        allowDropdown: true,
        countrySearch: true,
        formatAsYouType: true,
        formatOnDisplay: true,
        separateDialCode: false,
      });

      // Optionally adjust styles
      input.style.paddingLeft = '45px';

      // Add event listeners to track user interaction
      input.addEventListener('focus', () => {
        input.dataset.focused = "true";
      });

      input.addEventListener('blur', () => {
        input.dataset.focused = "false";
      });

      input.addEventListener('input', () => {
        input.dataset.userInteracted = "true";
        input.dataset.lastInteraction = Date.now();
      });

      // Store the instance on the input element for later access.
      input.itiInstance = itiInstance;

      return itiInstance;
    })
  );

  return iti_inputs;
};

/** 
 * Process form input submission values. Look for tel inputs and use intl-tel verification functions.
 * @param {HTMLFormElement} form
 * @param {Array} array of iti_intl inputs
 * @returns {Array[Object]} Returns a promise that resolves when processing is complete.
 */
export async function process_verify_tel_input_values(form_elements, error_function = window?.data_hub?.error_function) {
  if (!form_elements || form_elements.length === 0) {
    return error_function?.('No form provided to process_verify_tel_input_values', null);
  }

  let processed_phone_numbers = [];

  const errorMap = {
    0: "Invalid number",
    1: "Invalid country code",
    2: "Phone number too short",
    3: "Phone number too long",
    4: "Invalid number"
  };

  form_elements.forEach(form_element => {
    // Check for tel inputs and that the iti instance exists on the element.
    if (form_element.type === 'tel' && form_element.itiInstance) {
      const itiInstance = form_element.itiInstance;

      if (!itiInstance) {
        error_function?.('No intl-tel-input instance found on input: ' + (form_element?.name || form_element?.id), null);
        return;
      }

      const phoneNumber = itiInstance.getNumber();
      const isRequired = form_element.required || form_element.getAttribute('aria-required') === 'true';
      const isEmpty = !phoneNumber || phoneNumber.replace(/[+\s-]/g, '').length === 0;

      let this_phone_number_response = {
        domElement: form_element,
        phoneNumber: phoneNumber,
        isValid: itiInstance.isValidNumber(),
        error: null,
        errorMessage: null,
        country_code: null,
        country: null,
        isEmpty: isEmpty,
        isRequired: isRequired
      };

      // If field is not required and empty/minimal, consider it valid
      if (!isRequired && isEmpty) {
        this_phone_number_response.isValid = true;
      } else {

        const countryData = itiInstance.getSelectedCountryData();
        if (countryData && countryData.iso2 && typeof countryData.iso2 === 'string') {
          this_phone_number_response.country_code = countryData.iso2.toUpperCase();
        }
        if (countryData && countryData.name && typeof countryData.name === 'string') {
          this_phone_number_response.country = countryData.name;
        }

        if (!this_phone_number_response.isValid) {
          const validationErrorCode = itiInstance.getValidationError() || 0;
          const errorMessage = errorMap[validationErrorCode] || "Invalid number";
          this_phone_number_response.errorMessage = errorMessage;
          this_phone_number_response.error = validationErrorCode;
        }

      }

      processed_phone_numbers.push(this_phone_number_response);
    }
  });

  return processed_phone_numbers;
}

// Idempotent: module-level flag prevents adding the listener more than once if
// connectedCallback is invoked again (e.g. element re-mount, hot reload).
let _data_hub_new_user_data_listener_bound = false;
export const set_listener_for_new_user_data = async () => {
  if (_data_hub_new_user_data_listener_bound) return;
  _data_hub_new_user_data_listener_bound = true;
  document.addEventListener("data-hub-new-user-data", (e) => {
      listen_for_new_user_data_and_rebind_tel_inputs(e.detail);
  });
};

/**
 * Listen for the event data-hub-new-user-data, find our tel inputs, and update country_code if it exists in user_data
 * Respects user interaction - won't update fields the user is actively using
 */
export const listen_for_new_user_data_and_rebind_tel_inputs = (
  event_detail
) => {
  const data_hub_instance = event_detail?.data_hub_instance;
  const error_function = data_hub_instance?.error_function || window?.data_hub?.error_function;

  if (!event_detail) {
    return error_function?.('No event provided to listen_for_new_user_data_and_rebind_tel_inputs', null);
  }

  // Check if event_detail.user_data is null or {} empty
  const user_data = event_detail?.user_data && Object.keys(event_detail.user_data).length > 0
    ? event_detail.user_data
    : data_hub_instance?.user_data || window?.data_hub_user_data;

  if (!user_data) {
    return;
  }

  if (!data_hub_instance) {
    return error_function?.('No data hub instance provided to listen_for_new_user_data_and_rebind_tel_inputs', null);
  }

  try {

    const userCountryCode = user_data.country_code;
    
    const updateTimeout = data_hub_instance?.prefill_timeout || 2000;
    const currentTime = Date.now();

    if (!data_hub_instance?.iti_inputs || !data_hub_instance.iti_inputs.length) {
      return;
    }

    data_hub_instance.iti_inputs.forEach(itiInstance => {
      if (!itiInstance || !itiInstance.telInput) {
        return;
      }

      const inputElement = itiInstance.telInput;
      const userInteracted = inputElement.dataset.userInteracted === "true";
      const isCurrentlyFocused = inputElement.dataset.focused === "true";
      const lastInteraction = parseInt(inputElement.dataset.lastInteraction || 0);
      const timeElapsed = currentTime - lastInteraction;

      // Skip if user is actively interacting with this input
      if (userInteracted || isCurrentlyFocused || (lastInteraction && timeElapsed < updateTimeout)) {
        return;
      }

      const parsedNumber = itiInstance.getNumber(); // Current number in E.164 format
      const inputName = inputElement.name || inputElement.id;

      if (inputName && user_data[inputName]) {
        if (parsedNumber !== user_data[inputName]) {
          itiInstance.setNumber(user_data[inputName]);
        }
      }

      // Update the selected country if it differs from the userData country code
      if (userCountryCode && itiInstance?.getSelectedCountryData()?.iso2 && 
          itiInstance.getSelectedCountryData().iso2 !== userCountryCode.toLowerCase()) {
        itiInstance.setCountry(userCountryCode.toLowerCase());
      }
    });

  } catch (e) {
    error_function?.('Error listening for new user data:' + e, null);
  }
};

/**
 * Reset user interaction flags for telephone inputs, typically used after form submission/reset
 * @param {HTMLElement} container - The container with tel inputs to reset
 */
export const reset_tel_input_interaction_flags = (container) => {
  if (!container) return;

  const telInputs = container.querySelectorAll('input[type="tel"]');
  telInputs.forEach(input => {
    input.dataset.userInteracted = "false";
    input.dataset.focused = "false";
    delete input.dataset.lastInteraction;
  });
};

/**
 * Callback for prefill. If has intl-tel-input instance, look for inputs that have matching key->name values 
 * and call setNumber and setCountry.
 *
 * @param {Object} user_data - The user data object containing phone numbers and country codes.
 * @param {HTMLBaseElement} container - The container to search for tel inputs.
 * @param {Function} error_function - The function to handle errors (defaults to window.data_hub.error_function).
 */
export const prefill_refill_tel_inputs = (
  user_data,
  container,
  error_function = window?.data_hub?.error_function,
  data_hub_instance = window?.data_hub
) => {
  try {
    if (!container) {
      return error_function?.("No container provided to prefill_refill_tel_inputs", null);
    }

    const telInputs = container.querySelectorAll('input[type="tel"]');

    if (!telInputs || telInputs.length === 0) {
      return;
    }

    telInputs.forEach(inputElement => {
      const inputName = inputElement.name;
      if (!inputName) {
        return;
      }

      // Don't update if user has already interacted with this input
      const updateTimeout = data_hub_instance?.prefill_timeout || 2000; // 2 seconds timeout
      const currentTime = Date.now();
      const userInteracted = inputElement.dataset.userInteracted === "true";
      const isCurrentlyFocused = inputElement.dataset.focused === "true";
      const lastInteraction = parseInt(inputElement.dataset.lastInteraction || 0);
      const timeElapsed = currentTime - lastInteraction;

      if (userInteracted || isCurrentlyFocused || timeElapsed < updateTimeout) {
        return;
      }

      // Check if intl-tel-input is initialized
      if (!inputElement.dataset.intlTelInputInit || !inputElement.itiInstance) {
        if (user_data[inputName]) {
          inputElement.value = user_data[inputName];
        }
        return;
      }

      const itiInstance = inputElement.itiInstance;
      // If user data has a value for this input, set the formatted number
      if (user_data[inputName]) {
        itiInstance.setNumber(user_data[inputName]);
      }

      // Update country code if provided and valid
      if (user_data.country_code && typeof user_data.country_code === 'string') {
        let country_code = user_data.country_code.trim();
        if (country_code === '' || country_code.toLowerCase() === 'null' || country_code.toLowerCase() === 'undefined') {
          return;
        }
        const currentCountry = itiInstance?.getSelectedCountryData()?.iso2;
        const newCountryCode = country_code.toLowerCase();
        if (currentCountry !== newCountryCode) {
          itiInstance.setCountry(newCountryCode);
        } 
      }
    });
  } catch (e) {
    error_function?.(`Error in prefill_refill_tel_inputs: ${e.message}`, null);
  }
};